<script setup lang="ts">
import { RouterView } from 'vue-router'
import { onMounted, onUnmounted, watch, defineAsyncComponent, computed } from 'vue'
import { useUserStore } from './store/userStore'
import { storeToRefs } from 'pinia'
import { useRoute, useRouter } from 'vue-router'
import { useUser } from './composables/useOrgUsers'
import useUserSettings from './composables/useUserSettings'
import { useApi } from './config/apiFetcher'
import { onEvent } from './utils/EventBus'
import { viewsStore } from './store/views'
import { findDevice, normalizeUserGroups } from './utils/idi-utils'
import log from './utils/useLogger'
import AlertList from './components/AlertList.vue'
import RealtimeStatusBanner from './components/RealtimeStatusBanner.vue'
import useMqtt from './composables/useMqtt'
import { useBrowserNotification } from './composables/useBrowserNotification'
import { useQuery } from '@tanstack/vue-query'
import useApp from './composables/useApp'
import ConsentBanner from './components/ConsentBanner.vue'
import { updateSentryConsent } from './observability/sentry'
import type { ConsentPreferences } from './components/ConsentBanner.vue'
import { feedbackState } from './observability/feedbackState'
import { airframeHealthState } from './components/airframeHealthState'

// Lazy loaded components - modals, infrequently shown
const DeviceTakeoverModal = defineAsyncComponent(
  () => import('./components/DeviceTakeoverModal.vue'),
)
const InfoModal = defineAsyncComponent(() => import('./components/InfoModal.vue'))
const MediaUploadIndicator = defineAsyncComponent(
  () => import('./components/MediaUploadIndicator.vue'),
)
const FeedbackModal = defineAsyncComponent(() => import('./components/FeedbackModal.vue'))
const AirframeHealthModal = defineAsyncComponent(
  () => import('./components/AirframeHealthModal.vue'),
)
const TourMount = defineAsyncComponent(() => import('./components/onboarding/TourMount.vue'))

const api = useApi()
const route = useRoute()
const userStore = useUserStore()
const router = useRouter()
const {
  disconnectShadows,
  connectToShadows,
  connectSession,
  takeoverRequest,
  messageHandler,
  client,
  sessionClient,
  startHeartbeat,
  stopHeartbeat,
  cleanup,
  setUpHandler: setupMqttHandler,
} = useMqtt()
const { checkIfLoggedIn, triggerLogout, fetchDevices } = useUser()
const { requestPermission: requestNotificationPermission } = useBrowserNotification()
const { initUserSettings, forceSyncToCloud, cancelPendingSync } = useUserSettings()
const { showDeviceTakeoverModal, info, isMediaUploading } = storeToRefs(viewsStore())
const { organisationId, isLoggedIn, userId } = storeToRefs(userStore)
const {
  virtualStickInterval,
  selectedDevice,
  deviceGroupIDs,
  automation_state,
  selectedRC,
  selectedDock,
} = useApp()


/********************** EVENTS  ***************************/
onEvent('sessionExpired', () => {
  triggerLogout()
})
// Project-scoped device list (FLY3D-938): the query key tracks the active
// project, so switching projects refetches and repopulates the app store.
const devicesQuery = fetchDevices()

// After a project switch, drop a selected device group that is no longer in
// scope — the selectedDevice watcher below then tears down its MQTT shadows.
watch(
  () => devicesQuery.data.value,
  (devices) => {
    if (!devices || !selectedDevice.value) return
    const stillInScope = devices.some(
      (d: { deviceGroupID?: string }) => d.deviceGroupID === selectedDevice.value,
    )
    if (!stillInScope) {
      log.info('Selected device group left the active project scope — clearing selection')
      selectedDevice.value = null
    }
  },
)

// Handle consent updates
const handleConsentUpdate = (preferences: ConsentPreferences) => {
  updateSentryConsent(preferences)
  log.info('User consent updated', preferences)
}
/********************** HELPER FUNCTIONS  ***************************/
const isSetupPage = () =>
  route.path.includes('/setup/profile/') ||
  (route.path.includes('/setup/') && route.name === 'eula')

// The DEVICE client only. Safe to call on every device switch.
const setUpHandler = async () => {
  await connectToShadows(selectedRC.value, selectedDock.value, setupMqttHandler)
}

/**
 * The SESSION client, plus this browser's presence heartbeat (FLY3D-1029).
 *
 * Deliberately separate from setUpHandler: the session client carries the
 * user-scoped topics (takeover requests, assignment changes, presence) and
 * must NOT be rebuilt when the operator switches dock or drone. Rebuilding it
 * is what used to make a logged-on operator unreachable for several seconds
 * at a time, inside the window the old online check ran in.
 */
const setUpSession = async () => {
  await connectSession(setupMqttHandler)
  startHeartbeat()
}
// fetch device groupids
const { data: deviceGroupID } = useQuery({
  queryKey: computed(() => ['deviceGroupIds', organisationId.value, userId.value]),
  queryFn: async () => {
    const res = await api.get(
      `${import.meta.env.VITE_APP_HTTP_ENPOINT}/device-management/get-user-device-group?organisationID=${organisationId.value}&userID=${userId.value}`,
    )
    const groups = normalizeUserGroups(res.data)
    deviceGroupIDs.value = groups
    return groups
  },
  enabled: computed(() => !!organisationId.value && !!userId.value && isLoggedIn.value),
  staleTime: 1000 * 60 * 10,
  gcTime: 1000 * 60 * 20,
  retry: 2,
  retryDelay: 1000,
})

/********************** LIFECYCLE HOOKS  ***************************/
onMounted(async () => {
  try {
    if (isSetupPage()) return

    const loggedIn = await checkIfLoggedIn()
    if (loggedIn && organisationId.value) {
      await initUserSettings() // Load settings from IndexedDB (fast, no network)
      await setUpSession()
      await setUpHandler()
      // Ask once for permission to show browser notifications (e.g. takeover
      // requests) when the app is not in focus — FLY3D-866.
      requestNotificationPermission()
    }
  } catch (err) {
    log.error('Error during onMounted authentication check:', err)
  }
})
onUnmounted(() => {
  if (messageHandler.value) {
    log.info('unmounting handler for app page')
    client.value?.off('message', messageHandler.value!)
    sessionClient.value?.off('message', messageHandler.value!)
  }
  stopHeartbeat()
  cancelPendingSync()
  cleanup()
})
/********************** WATCHERS  ***************************/
watch(
  () => userId.value,
  async (newv, oldv) => {
    if (newv && newv !== oldv) {
      log.info('user ID changed, setting up handler')
      await initUserSettings() // Load settings from IndexedDB (fast)
      await setUpSession()
      await setUpHandler()
      requestNotificationPermission()
    }
    if (!newv) {
      // Sync settings to cloud before logout
      await forceSyncToCloud().catch(() => {})
      router.push({ name: 'Login' })
    }
  },
)
watch(
  () => selectedDevice.value,
  async (nv, ov) => {
    const prevRC = findDevice(ov, 'rc', false)
    const prevDock = findDevice(ov, 'dock', false)
    disconnectShadows(prevRC, prevDock)
    automation_state.value = null
    await setUpHandler()
  },
)
watch(
  () => route.name,
  (newv, oldVal) => {
    if (newv !== 'FlyDashboard' && virtualStickInterval.value)
      clearInterval(virtualStickInterval.value)
  },
)
</script>

<template>
  <RouterView v-slot="{ Component }">
    <keep-alive :include="['MapLayout']">
      <component :is="Component" />
    </keep-alive>
  </RouterView>
  <InfoModal v-if="info" />
  <AlertList />
  <!-- FLY3D-1029: tells the operator when they are unreachable, instead of
       failing silently and letting colleagues read them as offline. -->
  <RealtimeStatusBanner v-if="isLoggedIn" />
  <MediaUploadIndicator v-if="isMediaUploading" />
  <DeviceTakeoverModal
    v-if="showDeviceTakeoverModal && takeoverRequest"
    :request="takeoverRequest"
  />
  <ConsentBanner v-if="isLoggedIn" @consent-updated="handleConsentUpdate" />
  <FeedbackModal
    v-if="isLoggedIn"
    :is-open="feedbackState.isOpen"
    :start-minimized="feedbackState.startMinimized"
    :error-context="feedbackState.errorContext"
    @close="feedbackState.close()"
    @submitted="feedbackState.close()"
  />
  <!-- Airframe health popout (FLY3D-965). Mounted app-level so the minimised
       dock chip survives view changes, like the feedback modal. -->
  <AirframeHealthModal v-if="isLoggedIn && airframeHealthState.isOpen" />
  <TourMount v-if="isLoggedIn" />
</template>
